import { Body, VBadge, cn } from '@vrzl/ui';

import { answerSources, SourceButton } from '@/domains/overview';
import { CriterionFeedbackButton } from '@/domains/report';
import type { ValidationReportItem } from '@/domains/report/api/types';

import { matchesFilter } from '../utils/filterCriteria';

import { EvidenceInfo } from './evidenceInfo';

type CriterionRowProps = {
  item: ValidationReportItem;
  eventId: string;
  /** True once feedback was sent for this criterion in this session. */
  feedbackSent: boolean;
  onFeedbackSent: (criterionId: string) => void;
};

/**
 * One criterion in an open group: one table row per value, each with its own evidence
 * and sources. Criterion, feedback and status span all value rows.
 * A criterion that is not present gets a single row, with an error status.
 */
export function CriterionRow({ item, eventId, feedbackSent, onFeedbackSent }: CriterionRowProps) {
  const isMissing = matchesFilter(item, 'missing');
  const isInconsistent = item.consistent === false;
  const answers = isMissing ? [] : item.answers;
  const rowSpan = Math.max(answers.length, 1);

  const criterionCell = (
    <td rowSpan={rowSpan} className="px-5 py-3 wrap-anywhere">
      <Body tone="muted">{item.name}</Body>
    </td>
  );

  const trailingCells = (
    <>
      <td rowSpan={rowSpan} className="px-5 py-2">
        <CriterionFeedbackButton
          criterionId={item.id}
          criterionName={item.name}
          criterionCode={item.code}
          eventId={eventId}
          feedbackSent={feedbackSent}
          onFeedbackSent={onFeedbackSent}
        />
      </td>
      <td rowSpan={rowSpan} className="px-5 py-3">
        {isMissing ? (
          <VBadge color="error">Niet aanwezig</VBadge>
        ) : (
          <VBadge color={isInconsistent ? 'warning' : 'success'}>
            {isInconsistent ? 'Inconsistent' : 'Aanwezig'}
          </VBadge>
        )}
      </td>
    </>
  );

  if (answers.length === 0) {
    return (
      <tr className="border-t border-border align-top">
        {criterionCell}
        <td className="px-5 py-3">
          <Body tone="muted" className="italic">
            Niet aanwezig
          </Body>
        </td>
        <td className="px-5 py-2" />
        {trailingCells}
      </tr>
    );
  }

  return (
    <>
      {answers.map((answer, i) => {
        const isFirst = i === 0;
        // Lighter line between values of the same criterion, only on the value and source cells.
        const valueDivider = !isFirst && 'border-t border-border/50';
        return (
          <tr key={i} className={cn('align-top', isFirst && 'border-t border-border')}>
            {isFirst && criterionCell}
            <td className={cn('px-5 py-3 wrap-anywhere', valueDivider)}>
              <div className="flex items-start gap-2">
                <EvidenceInfo answer={answer} documents={item.documents} />
                <Body weight="medium" className="min-w-0 flex-1">
                  {answer.value ?? 'Niet aanwezig'}
                </Body>
              </div>
            </td>
            <td className={cn('px-5 py-2', valueDivider)}>
              {/* Icon with pages; the document name is in the tooltip (same as Overview). */}
              <div className="-ml-1.5 flex flex-wrap gap-0.5">
                {answerSources(answer, item.documents).map((source) => (
                  <SourceButton
                    key={source.docId}
                    eventId={eventId}
                    criterionId={item.id}
                    source={source}
                    highlightQuery={answer.value ?? undefined}
                  />
                ))}
              </div>
            </td>
            {isFirst && trailingCells}
          </tr>
        );
      })}
    </>
  );
}
